import React from "react";
import sortBy from "lodash/sortBy";
import { GetServerSideProps } from "next";

import { getBrands } from "api/requests/getBrands";
import { getBrandV3Show } from "api/requests/getBrandV3Show";
import { getIsFollowing } from "api/requests/getIsFollowing";
import getSearchResults from "api/requests/getSearchResults";
import getSponsoredArticles, {
  SponsoredArticle,
} from "api/requests/getSponsoredArticles";
import { getUserLocationData } from "api/requests/getUserLocation";
import searchProducts from "api/requests/searchProducts";
import { Action, Category, Label } from "constants/events";
import { Brand } from "custom-types/Brand";
import {
  BrandDetail,
  ProductCategory,
  ProductsByCategory,
} from "custom-types/BrandDetail";
import { MenuItem } from "custom-types/MenuItem";
import { useEventTracker } from "hooks/useEventTracker";
import { getCountryCodeHeader } from "utils/requestContext";

import AboutBrand from "components/brands/AboutBrand";
import BrandAdditionalInfo from "components/brands/BrandAdditionalInfo";
import BrandCoinCarousel from "components/brands/BrandCoinCarousel";
import BrandHead from "components/brands/BrandHead";
import BrandLayoutWrapper from "components/brands/BrandLayoutWrapper/BrandLayoutWrapper";
import BrandProductCategories from "components/brands/BrandProductCategories";
import BrandProductCoins from "components/brands/BrandProductCoins";
import BrandProductsNearYou from "components/brands/BrandProductsNearYou";
import ProductSpotlight from "components/brands/ProductSpotlight";
import { SponsoredArticlesCarousel } from "components/products/SponsoredArticlesCarousel";

export enum CountryCodes {
  US = "United States",
  CA = "Canada",
}

type BrandPageProps = {
  brand: BrandDetail;
  isFollowing: boolean;
  nearbyBrands: Brand[];
  nearbyProducts: MenuItem[];
  newBrands: Brand[];
  sponsoredArticles: SponsoredArticle[];
  productsForCategoryCarousels: ProductsByCategory;
  sortedCategoriesByCount: ProductCategory[];
  allBrands: Brand[];
  countryCode: "US" | "CA";
};

const BrandPage: React.FC<BrandPageProps> = ({
  brand,
  isFollowing,
  nearbyBrands,
  nearbyProducts,
  newBrands,
  sponsoredArticles,
  productsForCategoryCarousels,
  sortedCategoriesByCount,
  allBrands,
  countryCode,
}) => {
  const { publishEvent } = useEventTracker();
  const paidTier = brand.brandFeatureTier === "paid";
  const showAdditionalInfo =
    (brand.brandLicenses && brand.brandLicenses.length > 0) ||
    (paidTier && brand.informationWebsite);

  const filteredProductCategoryCountsByCountry = sortedCategoriesByCount.filter(
    (category) => category.country === CountryCodes[countryCode],
  );

  return (
    <>
      <BrandHead
        brand={brand}
        productsByCategory={productsForCategoryCarousels}
        sortedCategories={filteredProductCategoryCountsByCountry}
      />
      <BrandLayoutWrapper
        brand={brand}
        className="container"
        isFollowing={isFollowing}
      >
        {paidTier && (
          <div className="flex flex-col gap-xl">
            <AboutBrand
              brandId={brand.id}
              brandDescription={brand.description}
              brandSpotlightVideoUrl={brand.spotlightVideoEmbedUrl}
            />
            {brand.featuredProducts && (
              <ProductSpotlight
                brandId={brand.id}
                brandName={brand.name}
                brandSlug={brand.slug}
                featuredProducts={brand.featuredProducts}
              />
            )}
          </div>
        )}

        {!paidTier && (
          <>
            {nearbyBrands && nearbyBrands.length > 0 && (
              <BrandCoinCarousel
                title="Shop these nearby brands"
                brands={nearbyBrands}
                eventCategory={Category.brandInfo}
                eventLabel="top brands"
                disableLazyLoad
              />
            )}
            {newBrands && newBrands.length > 0 && (
              <div className="bg-leafly-white full-bleed-container py-xl">
                <div className="container">
                  <BrandCoinCarousel
                    title="Discover new brands"
                    brands={newBrands}
                    eventCategory={Category.brandInfo}
                    eventLabel="new brands"
                  />
                </div>
              </div>
            )}
            {allBrands && allBrands.length > 0 && (
              <BrandCoinCarousel
                title="Shop all brands"
                brands={allBrands}
                eventCategory={Category.brandInfo}
                eventLabel="all brands"
              />
            )}
          </>
        )}

        {nearbyProducts && nearbyProducts.length > 0 && (
          <BrandProductsNearYou
            brandName={brand.name}
            brandId={brand.id}
            products={nearbyProducts}
            brandSlug={brand.slug}
          />
        )}
        <BrandProductCoins
          sortedCategories={filteredProductCategoryCountsByCountry}
          brandSlug={brand.slug}
          brandId={brand.id}
          featureTier={brand.brandFeatureTier}
        />
        {sortedCategoriesByCount?.length > 0 && (
          <BrandProductCategories
            brandSlug={brand.slug}
            brandId={brand.id}
            featureTier={brand.brandFeatureTier}
            sortedCategories={filteredProductCategoryCountsByCountry}
            productsByCategory={productsForCategoryCarousels}
          />
        )}
        {paidTier && (
          <SponsoredArticlesCarousel
            sponsorId={brand.id}
            sponsorType="brand"
            title="Featured articles"
            className="m-0"
            showSeeMoreCard={false}
            initialArticles={sponsoredArticles}
            trackImpression={{
              category: Category.brandPaid,
              customDimensions: {
                brandId: brand.id,
              },
              label: Label.featuredArticle,
            }}
            onCardClick={(articleId?: number) =>
              publishEvent({
                action: Action.click,
                articleId,
                brandId: brand.id,
                category: Category.brandPaid,
                label: Label.featuredArticle,
              })
            }
          />
        )}
        {showAdditionalInfo && (
          <BrandAdditionalInfo
            brandId={brand.id}
            brandLicenses={brand.brandLicenses}
            informationWebsite={brand.informationWebsite}
            paidTier={paidTier}
          />
        )}
      </BrandLayoutWrapper>
    </>
  );
};

// @ts-expect-error -- TODO: Fix return type to adhere to props shape -- https://leafly.atlassian.net/browse/NL-1564
export const getServerSideProps: GetServerSideProps<BrandPageProps> = async (
  context,
) => {
  const { query } = context;
  const { slug } = query;

  const brand = await getBrandV3Show(String(slug));

  if (!brand) {
    return {
      notFound: true,
    };
  }

  const countryCode = getCountryCodeHeader(context.req.headers);

  const { coordinates } = await getUserLocationData(countryCode, context);

  const getNearbyProducts = async () => {
    if (coordinates && brand.brandFeatureTier === "paid") {
      const { menuItem } = await getSearchResults(
        {
          "filter[brand_slug]": brand.slug,
          "filter[max_distance]": "20",
          "filter[type]": "menu_item",
        },
        { lat: coordinates.latitude, lon: coordinates.longitude },
        20,
        countryCode,
        12,
        0,
        undefined,
        true,
      );
      return menuItem;
    }

    return [];
  };

  const sortedCategoriesByCount = sortBy(
    brand.productCategories,
    "productCount",
  ).reverse();

  const getProductsByCategory = async () => {
    const productsByCategory: ProductsByCategory = {};
    if (!brand.productCategories) {
      return productsByCategory;
    }

    const productsByCategoryArrays = await Promise.all(
      sortedCategoriesByCount.map(async (category) => {
        const { products } = await searchProducts(
          {
            filter: { brands: [brand.name], category: category.slug },
            take: 8,
          },
          countryCode,
          { lat: coordinates?.latitude, lon: coordinates?.longitude },
        );
        return products || [];
      }),
    );

    sortedCategoriesByCount.forEach((category, i) => {
      productsByCategory[category.slug] = productsByCategoryArrays[i];
    });

    return productsByCategory;
  };

  const [
    nearbyProducts,
    isFollowing,
    productsForCategoryCarousels,
    sponsoredArticles,
  ] = await Promise.all([
    getNearbyProducts(),
    getIsFollowing({
      countryCode,
      followeeId: brand?.id,
      followeeType: "brand",
    }),
    getProductsByCategory(),
    getSponsoredArticles({
      id: brand.id,
      sponsorType: "brand",
    }),
  ]);

  let nearbyBrands: Brand[] = [];
  let newBrands: Brand[] = [];
  let allBrands: Brand[] = [];
  if (coordinates && brand.brandFeatureTier === "info") {
    const params = {
      coordinates: {
        lat: coordinates.latitude,
        lon: coordinates.longitude,
      },
      countryCode,
      take: 10,
    };

    const [{ data: nearbyResults }, { data: newResults }] = await Promise.all([
      getBrands({
        ...params,
        filter: {
          "filter[brand_feature_tier]": "paid",
          "filter[max_distance]": "50mi",
        },
      }),
      getBrands({
        ...params,
        filter: {
          "filter[brand_feature_tier]": "paid",
          "filter[max_distance]": "50mi",
        },
        sort: {
          "sort[][created]": "asc",
        },
      }),
    ]);

    nearbyBrands = nearbyResults;
    newBrands = newResults;

    if (nearbyBrands.length === 0 && newBrands.length === 0) {
      const params = {
        countryCode,
        take: 10,
      };

      const [{ data: results }] = await Promise.all([
        getBrands({
          ...params,
          filter: {
            "filter[brand_feature_tier]": "paid",
          },
          sort: {
            "sort[][name]": "asc",
          },
        }),
      ]);

      allBrands = results;
    }
  }

  return {
    props: {
      allBrands,
      brand,
      countryCode,
      isFollowing,
      nearbyBrands,
      nearbyProducts,
      newBrands,
      productsForCategoryCarousels,
      sortedCategoriesByCount,
      sponsoredArticles,
    },
  };
};

export default BrandPage;
